import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view';
import {
    PAGE_BREAK_HEIGHT_PX,
    PAGE_CONTENT_HEIGHT_PX,
    PAGE_EDGE_OVERHANG_PX,
    PAGE_MARGIN_PX,
    PAGE_SHEET_CHROME_PX,
    PAGE_SHEET_GAP_PX,
} from '../utils/pageGeometry';

// Live pagination, the way a word processor does it: content that runs past
// the bottom of a page is pushed to the top of the next one, mid-paragraph if
// that's where the line falls.
//
// The breaks are *decorations*, never nodes. Nothing is written to the
// document, so pagination cannot dirty the autosave, land in the undo stack,
// or end up in an export. It is purely how the same text is drawn.
//
// The measuring pass works in "unpaginated" coordinates — rendered Y minus
// the height of every gap above it — so a pass sees the same geometry whether
// or not the previous pass already inserted gaps. Without that the gaps would
// feed back into the next measurement and walk down the document.

/**
 * 'sheets' fills out the rest of the page so the next one starts on a fresh
 * sheet of paper, the way Docs' print layout does. 'rule' just marks the
 * boundary with a line and carries on, the way Word's draft view does.
 */
export type PaginationMode = 'sheets' | 'rule';

export interface PaginationOptions {
    /** Usable content height of one page, in px (paper height minus margins) */
    pageHeight: number;
    /** Vertical space the break marker occupies in 'rule' mode */
    breakHeight: number;
    /** Bottom margin + gap between sheets + top margin, in 'sheets' mode */
    sheetChrome: number;
    /** The strip of desk visible between two sheets */
    sheetGap: number;
    /** A page's own margin, drawn as paper either side of the gap */
    pageMargin: number;
    /** How far a sheet gap reaches past the paper, covering its border */
    edgeOverhang: number;
    mode: PaginationMode;
    enabled: boolean;
    onPageCountChange?: (count: number) => void;
}

export const PaginationPluginKey = new PluginKey('pagination');

interface Break {
    pos: number;
    height: number;
    /** Sheets mode: paper above the gap — the page's unused tail plus its
     *  bottom margin. The gap strip starts after it. */
    padTop: number;
}

export interface Gap {
    top: number;
    height: number;
}

export interface Line {
    top: number;
    bottom: number;
    /** The writer put a page break before this line (Format > Line & spacing) */
    forced?: boolean;
}

const GAP_CLASS = 'pagination-gap';
/** Set by the ParagraphSpacing extension on blocks the writer broke before */
const FORCED_BREAK_CLASS = 'page-break-before';

/** Rendered Y -> unpaginated Y: subtract every gap above it. */
export const unpaginate = (renderedY: number, gaps: Gap[]): number => {
    let offset = 0;
    for (const gap of gaps) {
        if (gap.top + gap.height <= renderedY) offset += gap.height;
        else if (gap.top < renderedY) offset += renderedY - gap.top;
    }
    return renderedY - offset;
};

/**
 * Decide which lines start a new page.
 *
 * Walks the lines in order carrying `pageStart`, the unpaginated Y where the
 * current page's content begins. A line whose bottom would fall past the page
 * moves below the break marker whole, and the next page is measured from
 * there. Returns the index of every line that begins a page.
 */
export const planBreaks = (
    lines: Line[],
    pageHeight: number
): { index: number; leftover: number }[] => {
    const breaks: { index: number; leftover: number }[] = [];
    let pageStart = 0;

    lines.forEach((line, index) => {
        // A break the writer asked for happens whether or not the page is full
        if (!line.forced && line.bottom - pageStart <= pageHeight) return;
        // A line taller than a page has nowhere better to go; moving it would
        // just overflow the next page too (and loop forever). The same guard
        // stops a forced break at the very top of a page from breaking again.
        if (line.top <= pageStart) return;

        // How much of the page this line failed to fill — the empty space at
        // the foot of a real sheet. It floors at zero: a line that starts
        // *past* the page bottom (the one after something taller than a page,
        // or after a huge space-before) would otherwise ask the break to be
        // negative, and a negative CSS height is dropped outright — the gap
        // collapses to a hairline and the sheets stop parting.
        breaks.push({ index, leftover: Math.max(0, pageHeight - (line.top - pageStart)) });
        pageStart = line.top;
    });

    return breaks;
};

// One entry per visual line. Text nodes give per-line rects; a block with no
// text (image, empty paragraph, horizontal rule) counts as a single line.
const collectLines = (block: HTMLElement, originTop: number) => {
    const lines: { top: number; bottom: number; left: number }[] = [];
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    const range = document.createRange();

    let node = walker.nextNode();
    while (node) {
        if (node.textContent && node.textContent.length) {
            range.selectNodeContents(node);
            for (const rect of Array.from(range.getClientRects())) {
                if (rect.height > 0) {
                    lines.push({
                        top: rect.top - originTop,
                        bottom: rect.bottom - originTop,
                        left: rect.left,
                    });
                }
            }
        }
        node = walker.nextNode();
    }

    if (!lines.length) {
        const rect = block.getBoundingClientRect();
        if (rect.height > 0) {
            lines.push({ top: rect.top - originTop, bottom: rect.bottom - originTop, left: rect.left });
        }
        return lines;
    }

    // Fragments of the same line (a bold run mid-sentence) share a top edge
    lines.sort((a, b) => a.top - b.top);
    const merged: typeof lines = [];
    for (const line of lines) {
        const previous = merged[merged.length - 1];
        if (previous && Math.abs(previous.top - line.top) < 2) {
            previous.bottom = Math.max(previous.bottom, line.bottom);
            previous.left = Math.min(previous.left, line.left);
        } else {
            merged.push({ ...line });
        }
    }
    return merged;
};

const computeBreaks = (view: EditorView, options: PaginationOptions): Break[] => {
    const dom = view.dom as HTMLElement;
    const originTop = dom.getBoundingClientRect().top;

    const gaps: Gap[] = Array.from(dom.querySelectorAll<HTMLElement>(`.${GAP_CLASS}`)).map((el) => {
        const rect = el.getBoundingClientRect();
        return { top: rect.top - originTop, height: rect.height };
    });

    // Every visual line in the document, in unpaginated coordinates
    const lines: (Line & { left: number; renderedTop: number; renderedBottom: number })[] = [];
    for (const block of Array.from(dom.children) as HTMLElement[]) {
        if (block.classList.contains(GAP_CLASS)) continue;
        // Only the block's first line starts the new page; the rest of it
        // flows on normally.
        const forced = block.classList.contains(FORCED_BREAK_CLASS);
        collectLines(block, originTop).forEach((line, index) => {
            lines.push({
                top: unpaginate(line.top, gaps),
                bottom: unpaginate(line.bottom, gaps),
                left: line.left,
                renderedTop: line.top,
                renderedBottom: line.bottom,
                forced: forced && index === 0,
            });
        });
    }

    const breaks: Break[] = [];
    for (const planned of planBreaks(lines, options.pageHeight)) {
        const line = lines[planned.index];
        const pos = view.posAtCoords({
            left: line.left + 1,
            top: originTop + (line.renderedTop + line.renderedBottom) / 2,
        });
        if (!pos || pos.pos <= 0) continue;
        breaks.push({
            pos: pos.pos,
            // Sheets swallow the rest of the page so the next line lands at
            // the top of the following one; the rule just marks the spot.
            height: options.mode === 'sheets'
                ? planned.leftover + options.sheetChrome
                : options.breakHeight,
            padTop: planned.leftover + options.pageMargin,
        });
    }

    return breaks;
};

const buildDecorations = (breaks: Break[], doc: any, mode: PaginationMode, gapHeight: number) =>
    DecorationSet.create(
        doc,
        breaks.map((item) =>
            Decoration.widget(
                item.pos,
                () => {
                    // The child is the visible part — a dotted rule in
                    // pageless, the strip of desk between sheets in print
                    // layout. The parent supplies the surrounding paper.
                    const gap = document.createElement('div');
                    gap.className = `${GAP_CLASS} ${GAP_CLASS}--${mode}`;
                    gap.style.height = `${item.height}px`;
                    gap.contentEditable = 'false';
                    gap.setAttribute('aria-hidden', 'true');

                    const rule = document.createElement('div');
                    rule.className = `${GAP_CLASS}-rule`;
                    if (mode === 'sheets') {
                        // Paper down to the foot of the page, then the gap
                        gap.style.paddingTop = `${item.padTop}px`;
                        rule.style.height = `${gapHeight}px`;
                    }
                    gap.appendChild(rule);
                    return gap;
                },
                // side -1 keeps the gap above the line it pushed down; the key
                // lets ProseMirror reuse the DOM instead of rebuilding it
                {
                    side: -1,
                    key: `page-gap-${mode}-${item.pos}-${Math.round(item.height)}-${item.padTop}`,
                }
            )
        )
    );

/**
 * Margins that stretch a break's strip from wherever it sits out to a
 * reference edge — the paper for a sheet gap, the text column for a rule.
 *
 * A break inside a nested list is indented by every list above it, and CSS
 * cannot see how deep that is: fixed negative margins left the gap starting
 * partway across the page, so the sheets never fully parted. Measuring the
 * rendered offset handles any depth.
 */
export const stripMargins = (
    gap: { left: number; right: number },
    reference: { left: number; right: number },
    overhang: number
) => ({
    left: -(gap.left - reference.left) - overhang,
    right: -(reference.right - gap.right) - overhang,
});

const sameBreaks = (a: Break[], b: Break[]) =>
    a.length === b.length &&
    a.every((item, i) =>
        item.pos === b[i].pos
        && Math.abs(item.height - b[i].height) < 1
        && Math.abs(item.padTop - b[i].padTop) < 1);

/**
 * Are the breaks currently drawn the ones we just measured?
 *
 * Comparing a pass against the previous pass is not enough on its own. A
 * transaction can delete a widget without moving a pixel: ProseMirror drops a
 * widget sitting at a node's first position when that node is re-created, and
 * re-creating nodes is exactly what setNodeMarkup does — so applying spacing
 * to a paragraph deletes the page break at the top of it. The next pass then
 * measures the same geometry, computes the same plan, and would skip the
 * redraw that puts the break back, losing it for good.
 */
export const breaksAreDrawn = (breaks: { pos: number }[], drawn: number[]) =>
    breaks.length === drawn.length && breaks.every((item, i) => item.pos === drawn[i]);

export const Pagination = Extension.create<PaginationOptions>({
    name: 'pagination',

    addOptions() {
        return {
            pageHeight: PAGE_CONTENT_HEIGHT_PX,
            breakHeight: PAGE_BREAK_HEIGHT_PX,
            sheetChrome: PAGE_SHEET_CHROME_PX,
            sheetGap: PAGE_SHEET_GAP_PX,
            pageMargin: PAGE_MARGIN_PX,
            edgeOverhang: PAGE_EDGE_OVERHANG_PX,
            mode: 'sheets',
            enabled: true,
            onPageCountChange: undefined,
        };
    },

    addProseMirrorPlugins() {
        const options = this.options;

        return [
            new Plugin({
                key: PaginationPluginKey,

                state: {
                    init: () => DecorationSet.empty,
                    apply(tr, set) {
                        const next = tr.getMeta(PaginationPluginKey);
                        if (next) return next;
                        return set.map(tr.mapping, tr.doc);
                    },
                },

                props: {
                    decorations(state) {
                        return PaginationPluginKey.getState(state);
                    },
                },

                view(view) {
                    let frame: number | null = null;
                    let alignFrame: number | null = null;
                    let timer: ReturnType<typeof setTimeout> | null = null;
                    let lastBreaks: Break[] = [];
                    let lastWidth = 0;

                    let lastMode: PaginationMode = options.mode;

                    // Stretch each strip out to the paper edge (sheets) or
                    // the text column (rule), whatever it is nested inside.
                    const alignStrips = () => {
                        const page = view.dom.closest('.document-page') as HTMLElement | null;
                        const gaps = view.dom.querySelectorAll<HTMLElement>(`.${GAP_CLASS}`);
                        if (!gaps.length) return;

                        const sheetReference = page ? page.getBoundingClientRect() : null;
                        const ruleReference = view.dom.getBoundingClientRect();

                        gaps.forEach((gap) => {
                            const strip = gap.firstElementChild as HTMLElement | null;
                            if (!strip) return;
                            const sheets = gap.classList.contains(`${GAP_CLASS}--sheets`);
                            const reference = sheets ? sheetReference : ruleReference;
                            if (!reference) return;
                            const margins = stripMargins(
                                gap.getBoundingClientRect(),
                                reference,
                                // Sheets overhang the paper to cover its border
                                // and shadow; a rule stops at the text column.
                                sheets ? options.edgeOverhang : 0
                            );
                            strip.style.marginLeft = `${Math.round(margins.left)}px`;
                            strip.style.marginRight = `${Math.round(margins.right)}px`;
                        });
                    };

                    const scheduleAlign = () => {
                        if (alignFrame) cancelAnimationFrame(alignFrame);
                        alignFrame = requestAnimationFrame(alignStrips);
                    };

                    const measure = () => {
                        if (!options.enabled || !view.dom.isConnected) return;
                        const breaks = computeBreaks(view, options);
                        // Margins depend on layout, not just on the break list:
                        // dragging the ruler moves the edges without moving a
                        // single break.
                        scheduleAlign();
                        const drawn = PaginationPluginKey.getState(view.state) as DecorationSet | undefined;
                        // A mode switch changes how the same breaks are drawn;
                        // and the decorations have to be checked, not just the
                        // plan, or a break a transaction deleted never returns.
                        if (sameBreaks(breaks, lastBreaks)
                            && options.mode === lastMode
                            && breaksAreDrawn(breaks, (drawn?.find() ?? []).map((d) => d.from))) return;
                        lastMode = options.mode;
                        lastBreaks = breaks;
                        options.onPageCountChange?.(breaks.length + 1);
                        view.dispatch(view.state.tr.setMeta(
                            PaginationPluginKey,
                            buildDecorations(breaks, view.state.doc, options.mode, options.sheetGap)
                        ));
                        scheduleAlign();
                    };

                    // Measuring reads layout, so wait for the browser to be
                    // done with it — and coalesce bursts of typing.
                    const schedule = () => {
                        if (timer) clearTimeout(timer);
                        timer = setTimeout(() => {
                            if (frame) cancelAnimationFrame(frame);
                            frame = requestAnimationFrame(measure);
                        }, 150);
                    };

                    const onResize = () => {
                        const width = view.dom.clientWidth;
                        if (width === lastWidth) return;
                        lastWidth = width;
                        schedule();
                    };

                    const observer = typeof ResizeObserver !== 'undefined'
                        ? new ResizeObserver(onResize)
                        : null;
                    observer?.observe(view.dom);
                    schedule();

                    return {
                        update(updatedView, prevState) {
                            // Geometry only changes with the document (margin
                            // and spacing changes come through as transactions
                            // too) — a meta-only pagination tick must not
                            // schedule another pass or it never settles.
                            if (updatedView.state.doc !== prevState.doc) schedule();
                            else if (options.mode !== lastMode) schedule();
                        },
                        destroy() {
                            if (timer) clearTimeout(timer);
                            if (frame) cancelAnimationFrame(frame);
                            if (alignFrame) cancelAnimationFrame(alignFrame);
                            observer?.disconnect();
                        },
                    };
                },
            }),
        ];
    },
});
